import {
  IconButton,
  OverlayTriggerTooltip,
  PlusIcon,
  Pill,
  RepromptIcon,
  SubscriptionIcon,
} from '@utilities';

import styles from './_index.module.scss';

/**
 * Creates column definitions for the user subscription table.
 *
 * @param {Object} params - Parameters object.
 * @param {Object} [params.addingMap={}] - Map of user IDs to their adding state (boolean).
 * @param {Function} params.onAddToSubscription - Callback function invoked when adding a user to the subscription.
 * @param {Function} params.onRemoveFromSubscription - Callback function invoked when removing a user from the subscription.
 * @param {Object} [params.removingMap={}] - Map of user IDs to their removing state (boolean).
 * @param {Array<Object>} [params.tableData=[]] - Array of user data objects for the table.
 * @returns {Array<Object>} Array of column definition objects for the subscription table.
 */
export const createUserSubscriptionColumns = ({
  addingMap = {},
  canUpdateSubscription,
  onAddToSubscription,
  onRemoveFromSubscription,
  removingMap = {},
  tableData = [],
}) => [
  {
    accessor: 'name',

    // eslint-disable-next-line react/prop-types
    Cell: ({ row }) => {
      // eslint-disable-next-line react/prop-types
      const { id, isSubscribed, name, subscriptionId, type } = row.original;
      const isAdding = !!addingMap[id];
      const isRemoving = !!removingMap[id];

      const stop = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };

      return (
        <span className={styles['subscription-user']}>
          {isSubscribed ? (
            <OverlayTriggerTooltip
              content={canUpdateSubscription ? 'Remove user from the subscription' : ''}
              placement="top"
            >
              <IconButton
                aria-label={`Remove ${name} from subscription`}
                className={styles['subscription-remove-user-button']}
                disabled={!canUpdateSubscription || isRemoving}
                onClick={(e) => {
                  stop(e);
                  if (canUpdateSubscription)
                    onRemoveFromSubscription({ subscriptionId, userId: id });
                }}
                onMouseDown={stop}
                onMouseUp={stop}
              >
                {isRemoving ? (
                  <RepromptIcon className={styles['subscription-user-loading-icon']} />
                ) : (
                  <SubscriptionIcon className={styles['subscription-user-add-icon']} />
                )}
              </IconButton>
            </OverlayTriggerTooltip>
          ) : (
            <OverlayTriggerTooltip
              content={
                canUpdateSubscription
                  ? 'Add user to a subscription'
                  : 'Only admin can add to a subscription'
              }
              placement="top"
            >
              <IconButton
                aria-label={`Add ${name} to subscription`}
                className={styles['subscription-user-button']}
                disabled={!canUpdateSubscription || isAdding}
                onClick={(e) => {
                  stop(e);
                  if (canUpdateSubscription) onAddToSubscription(id);
                }}
                onMouseDown={stop}
                onMouseUp={stop}
              >
                {isAdding ? (
                  <RepromptIcon className={styles['subscription-user-loading-icon']} />
                ) : (
                  <PlusIcon className={styles['subscription-user-add-icon']} />
                )}
              </IconButton>
            </OverlayTriggerTooltip>
          )}

          <span className={styles['subscription-user-name-container']}>
            <OverlayTriggerTooltip content={name}>
              <span className={styles['subscription-user-name']}>{name}</span>
            </OverlayTriggerTooltip>
          </span>
          {type === 'admin' && <Pill isCompleted value="Admin" />}
          {type === 'survey_creator' && isSubscribed && <Pill isCompleted value="Survey Creator" />}
        </span>
      );
    },
    Footer: 'Responses Used',
    Header: 'Name',
    width: 300,
  },
  {
    accessor: 'subscriptionName',
    Cell: ({ row }) => row.original.subscriptionName,
    Header: 'Subscription',
  },
  {
    accessor: 'current',
    disableSortBy: true,
    Footer: () => <strong>{tableData.reduce((sum, row) => sum + row.current, 0)}</strong>,
    Header: 'Current Quarter',
  },
  {
    accessor: 'previous',
    disableSortBy: true,
    Footer: () => <strong>{tableData.reduce((sum, row) => sum + row.previous, 0)}</strong>,
    Header: 'Previous Quarter',
  },
];
